import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Cell, TableInstance } from 'react-table';
import { VariableSizeList } from 'react-window';
import { JsonSchema } from 'rxdb';

import {
  EditModeCell,
  EditModeConfig,
  EditModeTableInstance,
} from '@work4all/components/lib/dataDisplay/basic-table/plugins/useEditMode';

import { UseSettingReturn } from '@work4all/data/lib/settings';

import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { validateField } from '@work4all/utils';
import { DeepRequired } from '@work4all/utils/lib/deep-required/DeepRequired';

import { genericMemo } from '../../generic-memo';
import { useEditRowSeleciton } from '../../hooks/use-edit-row-selection';
import { InfiniteRow, useInifiteList } from '../../InfiniteList';
import {
  EditTableColumns,
  EditTableEntry,
  EditTableProps,
  NO_BORDER_KIND,
} from '../../types';
import { useResizableContext } from '../TableRow/use-resizable';

import { MemoizedCell } from './MemoizedCell';

interface TableCellContainerProps<T extends EditTableEntry>
  extends Pick<
    EditTableProps<T>,
    | 'showBorders'
    | 'disabled'
    | 'loadingRows'
    | 'noIdEditableCells'
    | 'rowSizeMode'
  > {
  row: InfiniteRow<T>;
  tablesLayoutBorders: UseSettingReturn<
    DeepRequired<{
      vertical?: boolean;
      horizontal?: boolean;
    }>
  >;
  columns: EditTableColumns<T>[];
  rowInEditModeValues: React.MutableRefObject<T>;
  editableCols: string[];
  setEditModeConfig: (config: EditModeConfig) => void;
  onRowEdit: (() => void) | null;
  skeletonFirstRowAffectedIndex: number;
  index: number;
  tableInstance: TableInstance<T> & EditModeTableInstance;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  listRef: React.MutableRefObject<VariableSizeList<any>>;
  isRowVisible: (index: number) => boolean;
  onRowOut?: (row: T, dir: 1 | -1) => void;
}

function TableCellContainerInternal<T extends EditTableEntry>(
  props: TableCellContainerProps<T>
) {
  const {
    row,
    showBorders,
    editableCols,
    tablesLayoutBorders,
    rowInEditModeValues,
    disabled,
    setEditModeConfig,
    onRowEdit,
    loadingRows,
    skeletonFirstRowAffectedIndex,
    noIdEditableCells,
    index,
    tableInstance,
    isRowVisible,
    onRowOut,
    rowSizeMode,
    columns,
  } = props;

  const noIdEditableCellsSet = useMemo(
    () => new Set(noIdEditableCells),
    [noIdEditableCells]
  );
  const loadingRowsSet = useMemo(() => new Set(loadingRows), [loadingRows]);

  const isDefaultExpanded = useMemo(() => {
    return (() => {
      const autoFocusColumn = columns.find(
        (x) => x.accessor === tableInstance.state.editModeConfig?.autoFocus
      );
      if (!autoFocusColumn) return false;

      if (
        autoFocusColumn.isLongText &&
        !row.original.cacheOnly &&
        autoFocusColumn.multiLinesRowKindsAllowed?.includes(
          row.original.positionKind
        )
      ) {
        return true;
      }
      return false;
    })();
  }, [columns, tableInstance.state.editModeConfig, row.original]);

  const resizeable = useResizableContext();
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (isDefaultExpanded && row.original.firstVisitFocus) {
      setTimeout(() => {
        toggleExpanded(true);
        row.original.firstVisitFocus = false;
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDefaultExpanded, row.original.firstVisitFocus]);

  const toggleExpanded = useCallback(
    (value?: boolean) => {
      setExpanded((x) => {
        if (value !== undefined) {
          if (!value) {
            resizeable.setHeight();
          }
          return value;
        }
        if (x && resizeable) {
          resizeable.setHeight();
        }
        return !x;
      });
    },
    [resizeable]
  );

  const { onSelectionChange, selectionRangeRef } = useEditRowSeleciton();
  const selectionRange = selectionRangeRef.current;
  const { getMaxSize } = useInifiteList();
  const rowInEditMode = useMemo(
    () =>
      row.cells.some((cell) => (cell as unknown as EditModeCell).isEditMode),
    [row.cells]
  );

  useEffect(() => {
    if (!rowInEditMode && expanded) {
      toggleExpanded(false);
    }
  }, [rowInEditMode, toggleExpanded, expanded]);

  const columnDefinitionsMap = useMemo(() => {
    const map = new Map<string, EditTableColumns<T>>();
    columns.forEach((column) => {
      if (typeof column.accessor === 'string') {
        map.set(column.accessor, column);
      }
    });
    return map;
  }, [columns]);

  const handleCellClick = useCallback(
    (cell: Cell<T>, _e: React.MouseEvent<HTMLElement> | HTMLElement) => {
      if (onRowEdit === null) return;

      const target = 'currentTarget' in _e ? _e.currentTarget : _e;
      const nestedInput = target.querySelector('textarea, input') as
        | HTMLTextAreaElement
        | HTMLInputElement;
      if (nestedInput)
        onSelectionChange(
          nestedInput.selectionStart,
          nestedInput.selectionEnd,
          cell.row.id,
          cell.column.id
        );
      if (disabled) {
        return;
      }
      if (cell.row.original.id === rowInEditModeValues.current?.id) return;

      if (rowInEditModeValues.current) onRowEdit();
      rowInEditModeValues.current = {
        id: row.original.id,
        localId: row.original.localId,
      } as T;

      setEditModeConfig({
        row: cell.row.id,
        columns: editableCols,
        autoFocus: cell.column.id,
      });
    },
    [
      onRowEdit,
      onSelectionChange,
      disabled,
      rowInEditModeValues,
      row,
      editableCols,
      setEditModeConfig,
    ]
  );

  const handleStopEdit = useCallback(
    (cell: Cell<T>) => {
      // Check if the onRowEdit function was passed
      if (onRowEdit === null) return;

      // Call functions passed in props or from hooks
      onRowEdit();
      rowInEditModeValues.current = null;
      onSelectionChange();

      // Set edit mode configuration using data from the passed cell
      return setEditModeConfig({
        row: cell.row.id,
        columns: [],
        autoFocus: cell.column.id,
      });
    },
    [
      // List all external dependencies used by the function
      onRowEdit,
      rowInEditModeValues,
      onSelectionChange,
      setEditModeConfig,
    ]
  );

  return (
    <React.Fragment>
      {row.cells.map((cell, idx) => {
        const definition =
          columnDefinitionsMap.get(cell.column.id as string) ??
          ({ type: 'number' } as EditTableColumns<T>);

        const isEditMode = (cell as unknown as EditModeCell).isEditMode;
        const isEditableCell = editableCols.includes(cell.column.id);
        const stopEdit = () => handleStopEdit(cell);
        const cellClick = (e: React.MouseEvent<HTMLElement>) =>
          handleCellClick(cell, e);

        const showSkeleton =
          (loadingRowsSet.has(row.original.id) ||
            (loadingRowsSet.size &&
              skeletonFirstRowAffectedIndex < index &&
              [
                ErpPositionsKind.ZWISCHENSUMME,
                ErpPositionsKind.TITELSUMME,
              ].includes(row.original.positionKind as ErpPositionsKind))) &&
          definition.skeleton;

        const isValueVisble = Boolean(
          row.original.id ||
            noIdEditableCellsSet?.has(cell.column.id) ||
            !(row.original.posId && cell.column.id === 'amount')
        );

        const autoFocus = selectionRange
          ? selectionRange.cellId === cell.column.id &&
            selectionRange.rowId === cell.row.id
          : undefined;
        const schema = getSchema(cell);
        const schemaValid = cell.value
          ? isFieldValid(cell.value, schema)
          : true;

        const baseStyle = {
          display: 'flex',
          alignItems: 'flex-start',
        };
        const { style, ...otherCellProps } = cell.getCellProps({
          style:
            tablesLayoutBorders.value.vertical && showBorders
              ? {
                  borderLeft:
                    NO_BORDER_KIND.includes(row.original.positionKind) ||
                    idx === 0
                      ? undefined
                      : '1px solid var(--ui04)',
                  ...baseStyle,
                }
              : baseStyle,
        });

        const cellClassStyle = definition.style;

        return (
          <MemoizedCell
            key={cell.column.id}
            cell={cell}
            cellIndex={idx}
            row={row}
            definition={definition}
            isEditMode={isEditMode}
            isEditableCell={isEditableCell}
            showSkeleton={showSkeleton}
            isValueVisible={isValueVisble}
            schemaValid={schemaValid}
            autoFocus={isRowVisible(cell.row.index) ? autoFocus : undefined}
            selectionRange={selectionRange}
            style={style}
            otherCellProps={otherCellProps}
            cellClassStyle={cellClassStyle}
            rowInEditMode={rowInEditMode}
            expanded={expanded}
            toggleExpanded={toggleExpanded}
            cellClick={cellClick}
            stopEdit={stopEdit}
            onSelectionChange={(start: number, end: number) => {
              onSelectionChange(start, end, cell.row.id, cell.column.id);
            }}
            onNext={(
              event: React.KeyboardEvent<HTMLTextAreaElement>,
              dir: 1 | -1
            ) => {
              const sortedCols =
                tableInstance.state.columnOrder.length ===
                tableInstance.visibleColumns.length
                  ? tableInstance.state.columnOrder
                  : tableInstance.visibleColumns.map((x) => x.id);
              const currentColumns = sortedCols.filter((x) =>
                editableCols.includes(x as string)
              );
              const idx = currentColumns.findIndex((x) => cell.column.id === x);

              const next = currentColumns[idx + dir];

              if (!next) {
                stopEdit();
                onRowOut?.(row.original, dir);
              }
            }}
            getMaxSize={getMaxSize}
            isRowVisible={isRowVisible}
            rowSizeMode={rowSizeMode}
            tableInstance={tableInstance}
            editableCols={editableCols}
            onRowOut={onRowOut}
            showBorders={showBorders}
            tablesLayoutBorders={tablesLayoutBorders}
          />
        );
      })}
    </React.Fragment>
  );
}

// This function checks if two string arrays have the same elements in the same order.
function areStringArraysEqual(a, b) {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}
function areTableContainerPropsEqual(prevProps, nextProps) {
  // 1. Compare `editableCols` based on content
  if (!areStringArraysEqual(prevProps.editableCols, nextProps.editableCols)) {
    return false; // Content is different, need to re-render
  }

  // 2. Compare remaining props in a standard, shallow way
  const allKeys = Object.keys({ ...prevProps, ...nextProps });
  for (const key of allKeys) {
    // Skip the already checked key `editableCols`
    if (key === 'editableCols') {
      continue;
    }

    // If references of other props are different, re-render
    if (prevProps[key] !== nextProps[key]) {
      return false;
    }
  }

  // 3. If no test showed differences, skip re-rendering
  return true;
}

export const TableCellContainer = genericMemo(
  TableCellContainerInternal,
  areTableContainerPropsEqual
);

function isFieldValid(value: unknown, schema: JsonSchema | null): boolean {
  if (schema) {
    const result = validateField(value, schema);
    return result.valid;
  }
  return true;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSchema<T extends object>(cell: Cell<T, any>) {
  if ('schema' in cell.column && cell.column.schema) {
    return cell.column.schema as JsonSchema;
  }
  return null;
}
